"use client";

import { useState } from "react";

type TabKey = "campaigns" | "keywords" | "ads" | "budget";

const tabs: { key: TabKey; label: string; count: number }[] = [
  { key: "campaigns", label: "Campaigns", count: 14 },
  { key: "keywords", label: "Keywords", count: 479 },
  { key: "ads", label: "Ad copy", count: 95 },
  { key: "budget", label: "Budget", count: 14 },
];

const tabCopy: Record<TabKey, { count: number; total: number; note: string; detail: string }> = {
  campaigns: { count: 14, total: 14, note: "High attention required", detail: "8 campaigns are constrained by eligible search volume. Review match coverage before adding budget." },
  keywords: { count: 38, total: 95, note: "Review opportunity coverage", detail: "38 keywords have declining quality signals. Consolidate overlapping match types before adjusting bids." },
  ads: { count: 11, total: 95, note: "Creative refresh recommended", detail: "One campaign has limited ad strength. Two new variants are ready for review." },
  budget: { count: 7, total: 14, note: "Pacing controls need review", detail: "7 campaigns risk month-end overage. Rebalance before the 24th to stay inside guardrails." },
};

type IssueKey = "volume" | "spend" | "ads";

const issues: Record<IssueKey, { icon: string; tone: string; title: string; detail: string; selected: string }> = {
  volume: { icon: "↗", tone: "blue", title: "Search volume limited", detail: "8 campaigns", selected: "8 campaigns are constrained by eligible search volume. Review match coverage before adding budget." },
  spend: { icon: "$", tone: "yellow", title: "Underspending", detail: "9 campaigns", selected: "9 campaigns are under pace. Move budget only where conversion quality stays above target." },
  ads: { icon: "!", tone: "coral", title: "Limited ad strength", detail: "1 campaign", selected: "One campaign has limited ad strength. Two new variants are ready for review." },
};

export function ExecutiveSummary() {
  const [tab, setTab] = useState<TabKey>("campaigns");
  const [issue, setIssue] = useState<IssueKey>("volume");
  const active = tabCopy[tab];

  return (
    <div className="mini-app app-summary">
      <div className="mini-app-head">
        <div>
          <span className="mini-label">Executive Summary · Account</span>
          <strong>Issues worth attention</strong>
        </div>
        <span className="mini-period">Today · 09:42</span>
      </div>

      <div className="summary-tabs" role="tablist" aria-label="Executive summary sections">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? "is-active" : ""}
            onClick={() => setTab(t.key)}
          >
            {t.label} <b>{t.count}</b>
          </button>
        ))}
      </div>

      <div className="summary-health">
        <div>
          <span className="mini-label">{tabs.find((t) => t.key === tab)?.label} with issues</span>
          <strong>
            {active.count} <small>/ {active.total}</small>
          </strong>
        </div>
        <div className="health-track">
          <span style={{ "--health": `${Math.min(100, (active.count / active.total) * 100)}%` } as React.CSSProperties} />
        </div>
        <em>{active.note}</em>
      </div>

      <div className="issue-grid">
        {Object.entries(issues).map(([key, issueItem]) => (
          <button
            key={key}
            type="button"
            className={`issue-card${issue === key ? " is-selected" : ""}`}
            onClick={() => setIssue(key as IssueKey)}
            aria-pressed={issue === key}
          >
            <span className={`issue-icon ${issueItem.tone}`} aria-hidden="true">
              {issueItem.icon}
            </span>
            <span>
              <b>{issueItem.title}</b>
              <small>{issueItem.detail}</small>
            </span>
          </button>
        ))}
      </div>

      <div className="summary-detail">{issues[issue].selected}</div>
    </div>
  );
}